import { useEffect, useRef } from 'react';
import { usePageContent } from '@/context/ContentContext';

export default function HeroSection() {
  const c = usePageContent('home');
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = heroRef.current?.querySelectorAll('.reveal');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={heroRef} className="relative w-full overflow-hidden bg-[#111] min-h-[420px] md:min-h-[520px]">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Multiple%20elegant%20house%20number%20plates%20scattered%20artfully%20on%20dark%20wooden%20surface%20with%20warm%20directional%20lighting%2C%20gold%20brass%20and%20black%20acrylic%20number%20plates%20with%20various%20numbers%20like%2048%2C%2014%2C%2021%2C%2031%2C%20some%20on%20dark%20textured%20wall%2C%20moody%20dramatic%20lighting%20with%20deep%20shadows%2C%20luxury%20product%20photography%20composition%2C%20dark%20charcoal%20background%20with%20warm%20amber%20light%20accents%2C%20premium%20signage%20aesthetic%2C%20cinematic%20dark%20atmosphere%20with%20golden%20highlights&width=1600&height=900&seq=hero-bg-2026&orientation=landscape"
          alt="Personalizovani kućni brojevi"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30" />
      </div>

      <div className="relative z-10 w-full px-4 md:px-6 pt-8 pb-12 md:pt-16 md:pb-24 flex flex-col justify-center max-w-[1450px] mx-auto min-h-[420px] md:min-h-[520px]">
        <div className="max-w-3xl">
          <div className="reveal opacity-0 translate-y-4 transition-all duration-700 ease-out">
            <span className="inline-flex items-center gap-2 px-4 py-2 border border-amber-400/40 rounded-full text-xs font-label text-amber-300 uppercase tracking-wider mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
              {c.hero_badge}
            </span>
          </div>

          <h1 className="reveal opacity-0 translate-y-4 transition-all duration-700 delay-100 ease-out font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-4">
            {c.hero_title}
          </h1>

          <p className="reveal opacity-0 translate-y-4 transition-all duration-700 delay-200 ease-out text-base md:text-lg text-white/80 max-w-xl mb-6 leading-relaxed">
            {c.hero_desc}
          </p>

          <div className="reveal opacity-0 translate-y-4 transition-all duration-700 delay-300 ease-out flex flex-col gap-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href="#products"
                className="inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-white text-foreground-950 font-label text-sm font-medium rounded-full hover:bg-white/90 transition-colors whitespace-nowrap"
              >
                {c.hero_cta1}
                <i className="ri-arrow-right-line" />
              </a>
              <a
                href="#about"
                className="inline-flex items-center justify-center gap-2 px-8 py-3.5 border border-white/40 text-white font-label text-sm font-medium rounded-full hover:bg-white/10 transition-colors whitespace-nowrap"
              >
                {c.hero_cta2}
              </a>
            </div>
            
            <div className="flex flex-wrap items-center gap-4 mt-2">
              <div className="flex items-center gap-2 text-white/80 text-sm">
                <div className="flex items-center gap-0.5">
                  <i className="ri-star-fill text-amber-400 text-xs" />
                  <i className="ri-star-fill text-amber-400 text-xs" />
                  <i className="ri-star-fill text-amber-400 text-xs" />
                  <i className="ri-star-fill text-amber-400 text-xs" />
                  <i className="ri-star-fill text-amber-400 text-xs" />
                </div>
                <span className="text-xs font-label">{c.hero_reviews}</span>
              </div>
              <div className="hidden sm:block w-px h-4 bg-white/30" />
              <div className="flex items-center gap-1.5 text-white/80 text-xs">
                <i className="ri-truck-line text-amber-400" />
                <span className="font-label">{c.hero_delivery}</span>
              </div>
              <div className="hidden sm:block w-px h-4 bg-white/30" />
              <div className="flex items-center gap-1.5 text-white/80 text-xs">
                <i className="ri-shield-check-line text-amber-400" />
                <span className="font-label">{c.hero_warranty}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .reveal.animate-in {
          opacity: 1;
          transform: translateY(0);
        }
      `}</style>
    </section>
  );
}